/**
 * Shared constants for the Adult Autism review pages (SSI Review and
 * Observation Review).
 *
 * Why this file exists
 * --------------------
 * Both the Semi-Structured Interview (SSI) review page and the Behavioural
 * Observation review page present the same clinical-decision structure that the
 * ADHD app uses on its review pages:
 *
 *   1. "Condition(s) Referred For"   — the primary condition the patient was
 *      referred to be assessed for. For the Adult Autism pathway this is always
 *      Autism Spectrum Disorder (ASD), so it is a fixed, non-removable entry
 *      rather than something the clinician picks from a list.
 *   2. "Emerging Differentials"      — other conditions the clinician is
 *      actively considering as alternative or co-occurring explanations.
 *
 * Previously ASD lived inside the differentials picker (pinned to the top).
 * Splitting ASD out into its own "Referred For" section means the differentials
 * list must NOT contain ASD any more, otherwise the same condition would appear
 * in two places. Centralising both the ASD label and the differentials list
 * here guarantees the two review pages stay in lock-step and there is a single
 * source of truth to update if the curated list ever changes.
 */

import { AccordionData } from './data_DF';

/**
 * The canonical, human-readable label for the condition every Adult Autism
 * patient is referred to be assessed for. Used as the fixed entry in the
 * "Condition(s) Referred For" panel and as the migration key when moving
 * legacy ASD differential notes into their new home (see the review pages).
 */
export const ASD_CONDITION_LABEL = 'Autism Spectrum Disorder (ASD)';

/**
 * Curated list of differential / co-occurring conditions offered to the
 * clinician in the "Emerging Differentials" picker on the Adult Autism review
 * pages.
 *
 * IMPORTANT: ASD is deliberately absent. It is handled separately by the
 * "Condition(s) Referred For" panel (see ASD_CONDITION_LABEL). The remaining
 * entries cover the most common differentials seen in adult autism pathways.
 */
export const AUTISM_DIFFERENTIAL_CONDITIONS = [
  'Attention Deficit Hyperactivity Disorder (ADHD)',
  'Social Anxiety Disorder',
  'Generalised Anxiety Disorder',
  'Depressive Disorders',
  'Personality Disorder',
  'Obsessive-Compulsive Disorder',
  'Post-Traumatic Stress Disorder',
  'Bipolar Disorder',
  'Eating Disorders',
  'Substance Use Disorder',
];

/**
 * Returns the full, categorised ICD-11 condition list used by the ADHD app
 * (`AccordionData`), with Autism Spectrum Disorder removed.
 *
 * Why this exists
 * ---------------
 * The Adult Autism SSI Review and Observation Review pages now use the SAME
 * "select many" differentials table the ADHD app uses (`ConditionSelectionPanelTOC`
 * fed by `AccordionData`), rather than the bespoke per-condition-notes picker.
 * For an Autism assessment, ASD is the "condition referred for" and is shown in
 * its own read-only panel, so it must NOT appear in the emerging differentials
 * picker. Everything else from `AccordionData` (including ADHD, which is a common
 * and clinically important differential for autism) remains selectable.
 *
 * The shape returned is `{ [category: string]: Array<{ icd_11_label, ... }> }`,
 * exactly matching what `ConditionSelectionPanelTOC` expects via its `conditions`
 * prop. Categories that become empty after filtering are dropped so the picker
 * never renders an empty section header.
 *
 * @returns {Object<string, Array<Object>>} Categorised conditions minus ASD.
 */
export function getAutismDifferentialConditions() {
  const result = {};
  for (const [category, conditions] of Object.entries(AccordionData)) {
    const filtered = conditions.filter(
      (c) => c.icd_11_label !== ASD_CONDITION_LABEL,
    );
    if (filtered.length > 0) result[category] = filtered;
  }
  return result;
}
